import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";

import { userService } from "@/app/api/user";
import { useIdentifyUserAnalyticsEffect } from "@/hooks/analytics.hooks";
import { useIntercom } from "@/hooks/use-intercom";
import {
  MFAStatus,
  type User,
  type UserContextType,
  UserRole,
} from "@/types/user";
import type { Nullable } from "@/types/utils";
import { initializeAdmin } from "@/utils/extension";

const UserContext = createContext<UserContextType>({
  canSendCandidatePdfs: false,
  capabilities: [],
  company: null,
  initialized: false,
  isInternal: false,
  login() {
    throw new Error("Attempted to log in before user context initialized");
  },
  logOut() {},
  refreshUser() {},
  role: UserRole.Reviewer,
  user: null,
});

export const UserContextProvider = ({ children }: PropsWithChildren) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<Nullable<User>>(null);
  const [initialized, setInitialized] = useState(false);

  const login = async (
    email: string,
    password: string,
    mfaToken?: string,
    phone?: string,
  ) => {
    return userService
      .login(email, password, mfaToken, phone)
      .then((user) => {
        setUser(user);
        initializeAdmin({ atsDomain: user.company.atsDomain || "" });

        return { status: MFAStatus.Success };
      })
      .catch((error) => {
        if (
          error?.response?.data?.status &&
          [MFAStatus.MFARequired, MFAStatus.MFASetupRequired].includes(
            error.response.data.status,
          )
        ) {
          return {
            maskedDestination: error.response.data.maskedDestination,
            status: error.response.data.status,
            // Short-lived token authorizing the follow-up /auth/mfa call during
            // the login MFA setup flow (SC-125).
            mfaEnrollmentToken: error.response.data.mfaEnrollmentToken,
          };
        }

        if (error.status === 403 || error.status === 401) {
          logOut();
          initializeAdmin({ atsDomain: "" });
        }

        throw error;
      });
  };

  const logOut = () => {
    userService.logOut();
    setUser(null);
    queryClient.clear();
    router.push("/auth/login");
  };

  const refreshUser = () => {
    if (user) {
      userService.getUser(user.id).then((user) => {
        setUser(user);
      });
    }
  };

  useIdentifyUserAnalyticsEffect(user);
  useIntercom(user);

  useEffect(() => {
    userService
      .initialize(logOut)
      .then((user) => {
        if (user) {
          setUser(user);
        }

        initializeAdmin({ atsDomain: user?.company.atsDomain ?? "" });

        // On load, if the user is not logged in and is not on one of the auth routes, redirect them to login
        if (!user && !window.location.pathname.startsWith("/auth")) {
          router.push("/auth/login");
        }
      })
      .catch(() => {
        initializeAdmin({ atsDomain: "" });
      })
      .finally(() => setInitialized(true));
    // We want this to run on first init only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <UserContext.Provider
      value={{
        canSendCandidatePdfs: user?.canSendCandidatePdfs ?? false,
        capabilities: user?.capabilities ?? [],
        company: user?.company ?? null,
        initialized,
        isInternal: user?.isInternal ?? false,
        login,
        logOut,
        refreshUser,
        role: user?.role ?? UserRole.Reviewer,
        user,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  return useContext(UserContext);
};

export enum LoginState {
  Initial = "initial",
  MFARequired = MFAStatus.MFARequired,
  MFASetupRequired = MFAStatus.MFASetupRequired,
}
